import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight, FileEdit, GitMerge, History, Plus, ShieldAlert, X } from 'lucide-react';
import CircleForm from './CircleForm';
import { useCircleColors } from './structure/colors';
import { FocusField, MentionText, splitFocusProjects } from './structure/focus';
import { DomainsList, ReservedDomainChips } from './structure/ReservedDomains';
import { OwnerEditor, StrategyEditor } from './structure/editors';
import { MeetingMenu, SettingsMenu } from './structure/menus';
import { Avatar, Bullets, LinkButton, Section } from './structure/ui';
import CircleMetrics from './structure/CircleMetrics';

const ownersOf = (circle) => {
  const ids = [];
  const push = (value) => {
    const id = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(id) || ids.includes(id)) return;
    ids.push(id);
  };
  push(circle?.owner_id);
  (circle?.owner_ids || []).forEach(push);
  return ids;
};

const statusLabel = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'in_progress') return 'En cours';
  if (value === 'done' || value === 'completed') return 'Terminé';
  if (value === 'paused' || value === 'on_hold') return 'En pause';
  return status || '';
};

const StructurePanel = ({
  isEditing,
  selectedCircle,
  employees,
  parentId,
  isRole,
  isLeafRole,
  canAddSubCircle,
  canEditLeader,
  canEditStrategy,
  canEditVoteTimeout,
  canEditColor,
  canEditRoleFocus,
  activeEmployees,
  findEmployee,
  onAddSubCircle,
  onStartTension,
  onSave,
  onSavePatch,
  onDelete,
  onCancelEdit,
  onConsolidateRoots,
  onOpenMeeting,
  onJoinMeeting,
  onOpenMeetingHistory,
  meetingLoading,
  openMeetingsByCircle = new Map(),
  isSuperEditor,
  superEditMode,
  onToggleSuperEdit,
  onStartSuperEdit,
  circleMemberIds = [],
  directRoles = [],
  roleFocusMap = {},
  onSaveRoleFocus,
  mentionItems = [],
  circles = [],
  projects = [],
  onSelectCircle,
  selectedEmployeeId,
  onSelectEmployee,
  onClearEmployee,
  onOpenProject,
  projectsInScope = [],
  canViewProjects = true,
  onExportOrganization,
  organizationExporting = false,
  metricsRange,
  metricsRangeValid,
  onMetricsRangeChange,
  currentSlotRange,
  isUsingSlotRange,
  circleMetricsSummary,
  circleMetricsLoading,
  circleMetricsError,
  error,
  onDismissError,
}) => {
  const [editing, setEditing] = useState(null); // owner | strategy
  const { getMentionStyle } = useCircleColors(circles);
  const circleById = useMemo(() => new Map((circles || []).filter((c) => c?.id).map((c) => [Number(c.id), c])), [circles]);
  const projectById = useMemo(() => new Map((projects || []).filter((p) => p?.id).map((p) => [Number(p.id), p])), [projects]);
  const childCount = useMemo(() => {
    const counts = new Map();
    (circles || []).forEach((circle) => {
      if (circle?.parent_id === null || circle?.parent_id === undefined || circle?.parent_id === '') return;
      counts.set(String(circle.parent_id), (counts.get(String(circle.parent_id)) || 0) + 1);
    });
    return counts;
  }, [circles]);
  const isRoleCircle = useCallback(
    (circle) => Boolean(circle?.parent_id) && !childCount.get(String(circle.id)),
    [childCount],
  );

  useEffect(() => {
    setEditing(null);
  }, [selectedCircle?.id]);

  const mentionProps = { circleById, projectById, onOpenProject, onSelectCircle, getMentionStyle };
  const focusMentionItems = useMemo(() => mentionItems.filter((item) => item.type !== 'rule'), [mentionItems]);
  const openMeeting = selectedCircle?.id ? openMeetingsByCircle.get(String(selectedCircle.id)) : null;
  const ancestors = useMemo(() => {
    const list = [];
    let current = selectedCircle?.parent_id ? circleById.get(Number(selectedCircle.parent_id)) : null;
    const seen = new Set();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      list.unshift(current);
      current = current.parent_id ? circleById.get(Number(current.parent_id)) : null;
    }
    return list;
  }, [selectedCircle, circleById]);
  const subCircles = useMemo(
    () => (selectedCircle?.id ? (circles || []).filter((c) => String(c.parent_id) === String(selectedCircle.id) && !isRoleCircle(c)) : []),
    [circles, selectedCircle?.id, isRoleCircle],
  );
  const inProgressProjects = useMemo(
    () => (projectsInScope || []).filter((p) => String(p?.status || '').toLowerCase() === 'in_progress'),
    [projectsInScope],
  );
  const employeeRoles = useMemo(() => {
    if (selectedEmployeeId === null || selectedEmployeeId === undefined) return [];
    const id = Number(selectedEmployeeId);
    return (circles || [])
      .filter((circle) => circle?.id && isRoleCircle(circle) && ownersOf(circle).includes(id))
      .map((role) => ({ ...role, parentName: circleById.get(Number(role.parent_id))?.name || '' }))
      .sort((a, b) => (a.parentName || '').localeCompare(b.parentName || '', 'fr') || (a.name || '').localeCompare(b.name || '', 'fr'));
  }, [selectedEmployeeId, circles, circleById, isRoleCircle]);

  const renderFocus = (roleId, employeeId, lead) => (
    <FocusField
      value={roleFocusMap[`${roleId}:${employeeId}`] || ''}
      canEdit={canEditRoleFocus}
      onSave={(next) => onSaveRoleFocus && onSaveRoleFocus(roleId, Number(employeeId), next)}
      mentionItems={focusMentionItems}
      getMentionStyle={getMentionStyle}
      mentionProps={mentionProps}
      placeholder="Sur quoi porte son rôle ? (@projet)"
      lead={lead}
    />
  );

  const errorBanner = error ? (
    <div className="mx-4 mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span className="flex-1">{error}</span>
      {onDismissError && (
        <button type="button" onClick={onDismissError} className="text-rose-400 hover:text-rose-700" aria-label="Fermer">
          <X size={14} />
        </button>
      )}
    </div>
  ) : null;

  if (isEditing) {
    return (
      <div className="h-full flex flex-col min-h-0">
        {errorBanner}
        <CircleForm
          circle={selectedCircle}
          parentCircle={circleById.get(Number(parentId)) || null}
          activeEmployees={activeEmployees}
          findEmployee={findEmployee}
          mentionItems={mentionItems}
          getMentionStyle={getMentionStyle}
          onSave={onSave}
          onDelete={onDelete}
          onCancel={onCancelEdit}
          parentId={parentId}
          isRole={isRole}
        />
      </div>
    );
  }

  const shell = (children) => (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 h-full overflow-y-auto">
      {errorBanner}
      {children}
    </div>
  );

  if (selectedEmployeeId !== null && selectedEmployeeId !== undefined) {
    const person = findEmployee(selectedEmployeeId);
    return shell(
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar employee={person || { name: '?' }} size={44} />
            <div>
              <h2 className="text-xl font-bold text-slate-800">{person?.name || 'Personne inconnue'}</h2>
              <p className="text-sm text-slate-500">{employeeRoles.length} rôle{employeeRoles.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          {onClearEmployee && (
            <button
              type="button"
              onClick={onClearEmployee}
              className="text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50"
            >
              Retour au cercle
            </button>
          )}
        </div>
        {employeeRoles.length > 0 ? (
          <div className="space-y-2">
            {employeeRoles.map((role) => (
              <div key={role.id} className="rounded-xl border border-slate-200 p-3 flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-semibold text-slate-800">
                    {role.name}
                    {role.parentName && <span className="font-normal text-slate-400"> · {role.parentName}</span>}
                  </p>
                  <ReservedDomainChips keys={role.reserved_domains} />
                  <FocusField
                    value={roleFocusMap[`${role.id}:${selectedEmployeeId}`] || ''}
                    canEdit={false}
                    mentionProps={mentionProps}
                    getMentionStyle={getMentionStyle}
                  />
                </div>
                {onSelectCircle && (
                  <LinkButton
                    onClick={() => {
                      onSelectCircle(role);
                      onClearEmployee && onClearEmployee();
                    }}
                  >
                    Voir le rôle
                  </LinkButton>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Aucun rôle.</p>
        )}
      </div>,
    );
  }

  if (!selectedCircle) {
    return shell(
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-slate-400">
        Choisis un cercle ou un rôle sur la carte pour voir sa fiche.
      </div>,
    );
  }

  if (!selectedCircle.id) {
    return shell(
      <div className="flex flex-col items-center justify-center h-full text-center space-y-4 p-8">
        <h2 className="text-xl font-bold text-slate-800">Plusieurs cercles racines</h2>
        <p className="text-sm text-slate-500 max-w-xs">
          « Organisation » n'existe pas vraiment : elle regroupe les cercles sans parent. Un administrateur peut les ranger sous un
          cercle d'ancrage unique.
        </p>
        {isSuperEditor && (
          <button
            type="button"
            onClick={onConsolidateRoots}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold"
          >
            Regrouper sous un cercle d'ancrage
          </button>
        )}
      </div>,
    );
  }

  const owners = ownersOf(selectedCircle);
  const typeLabel = isLeafRole ? 'Rôle' : selectedCircle.parent_id ? 'Cercle' : "Cercle d'ancrage";
  const hasReserved = (selectedCircle.reserved_domains || []).length > 0;

  return shell(
    <>
      {superEditMode && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-800">
          <ShieldAlert size={14} />
          <span className="font-semibold">Super édition</span>
          <span className="text-amber-700">modifications directes, sans gouvernance.</span>
          <span className="ml-auto flex gap-2">
            <button type="button" onClick={onStartSuperEdit} className="rounded-lg bg-white px-2.5 py-1 font-semibold border border-amber-200 hover:bg-amber-100">
              <FileEdit size={12} className="inline mr-1" />
              Modifier la fiche
            </button>
            {canAddSubCircle && !isLeafRole && (
              <button type="button" onClick={onAddSubCircle} className="rounded-lg bg-white px-2.5 py-1 font-semibold border border-amber-200 hover:bg-amber-100">
                <Plus size={12} className="inline mr-1" />
                Ajouter un rôle
              </button>
            )}
          </span>
        </div>
      )}

      <header className="px-5 pt-4 pb-3 border-b border-slate-100 space-y-2">
        {ancestors.length > 0 && (
          <nav className="flex flex-wrap items-center gap-0.5 text-xs text-slate-400">
            {ancestors.map((circle) => (
              <React.Fragment key={circle.id}>
                <button type="button" onClick={() => onSelectCircle && onSelectCircle(circle)} className="hover:text-slate-700">
                  {circle.name}
                </button>
                <ChevronRight size={12} />
              </React.Fragment>
            ))}
          </nav>
        )}
        <div className="flex items-start gap-2">
          <h2 className="text-xl font-bold text-slate-800 break-words min-w-0">{selectedCircle.name}</h2>
          <span className={`mt-1 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            isLeafRole ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'
          }`}
          >
            {typeLabel}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isRole && (
            <MeetingMenu
              circle={selectedCircle}
              openMeeting={openMeeting}
              loading={meetingLoading}
              onOpenMeeting={onOpenMeeting}
              onJoinMeeting={onJoinMeeting}
            />
          )}
          <button
            type="button"
            onClick={() => onStartTension(selectedCircle)}
            className="px-3 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg hover:bg-indigo-100 flex items-center gap-1.5"
            title="Une tension : ce qui coince et le changement de structure que tu proposes"
          >
            <GitMerge size={14} />
            Proposer un changement
          </button>
          {!isRole && onOpenMeetingHistory && (
            <button
              type="button"
              onClick={() => onOpenMeetingHistory(selectedCircle)}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
            >
              <History size={14} />
              Historique
            </button>
          )}
          <span className="ml-auto">
            <SettingsMenu
              circle={selectedCircle}
              voteTimeoutDays={selectedCircle.vote_timeout_days}
              color={selectedCircle.color || ''}
              showVotes={!isLeafRole}
              canEditVoteTimeout={canEditVoteTimeout}
              canEditColor={canEditColor}
              onSavePatch={onSavePatch}
              isSuperEditor={isSuperEditor}
              superEditMode={superEditMode}
              onToggleSuperEdit={onToggleSuperEdit}
              onExportOrganization={onExportOrganization}
              organizationExporting={organizationExporting}
            />
          </span>
        </div>
      </header>

      <div className="p-5 space-y-6">
        <Section title="Raison d'être">
          <p className={`text-sm ${selectedCircle.purpose ? 'text-slate-800' : 'text-slate-400'}`}>
            {selectedCircle.purpose || 'Pas encore définie.'}
          </p>
        </Section>

        <Section
          title={isLeafRole ? 'Titulaires et focus' : 'Leader'}
          hint={isLeafRole ? "Le nombre de projets en cours, c'est le focus : sur quels projets chacun tient le rôle (et où s'appliquent ses domaines à effet). Les projets terminés restent dans le focus, repliés." : null}
          action={canEditLeader && editing !== 'owner' ? (
            <LinkButton onClick={() => setEditing('owner')}>{isLeafRole ? 'Changer les titulaires' : 'Changer'}</LinkButton>
          ) : null}
        >
          {editing === 'owner' ? (
            <OwnerEditor
              isRole={isLeafRole}
              ownerIds={owners}
              activeEmployees={activeEmployees}
              findEmployee={findEmployee}
              onSave={onSavePatch}
              onClose={() => setEditing(null)}
            />
          ) : owners.length > 0 ? (
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
              {owners.map((id) => {
                const person = findEmployee(id);
                const who = (
                  <span className="flex min-w-0 items-center gap-2.5">
                    <Avatar employee={person} size={26} onClick={person ? () => onSelectEmployee && onSelectEmployee(person.id) : undefined} />
                    <span className="truncate text-sm font-semibold text-slate-800">{person?.name || 'Personne inconnue'}</span>
                  </span>
                );
                return (
                  <div key={id} className="px-3 py-2">
                    {isLeafRole ? renderFocus(selectedCircle.id, id, who) : who}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400">{isLeafRole ? 'Personne ne tient ce rôle.' : 'Pas de leader.'}</p>
          )}
        </Section>

        <Section title="Redevabilités">
          <Bullets
            items={selectedCircle.accountabilities}
            color="bg-emerald-500"
            render={(item) => <MentionText text={item} {...mentionProps} />}
            empty="Aucune redevabilité."
          />
        </Section>

        <Section
          title="Domaines"
          hint={isLeafRole && !hasReserved
            ? "Un domaine peut avoir un effet dans Deepflow (🔑 Roadmap, Facturabilité…) : il réserve un droit aux titulaires du rôle."
            : !isLeafRole && hasReserved
              ? "Un domaine à effet du cercle est tenu par son leader sur tous les projets du cercle, sans focus, sauf ceux qu'un rôle tient par son focus."
              : null}
        >
          <DomainsList
            circle={selectedCircle}
            ownerIds={owners}
            roleFocusMap={roleFocusMap}
            projectById={projectById}
            findEmployee={findEmployee}
            onOpenProject={onOpenProject}
            mentionProps={mentionProps}
            isCircle={!isLeafRole}
            circles={circles}
            projects={projects}
          />
        </Section>

        <p className="-mt-3 text-[11px] text-slate-400">
          Raison d'être, redevabilités et domaines changent par la gouvernance : « Proposer un changement ».
        </p>

        {!isLeafRole && (
          <Section
            title="Stratégie"
            action={canEditStrategy && editing !== 'strategy' ? <LinkButton onClick={() => setEditing('strategy')}>Modifier</LinkButton> : null}
          >
            {editing === 'strategy' ? (
              <StrategyEditor
                items={selectedCircle.strategy || []}
                onSave={(strategy) => onSavePatch({ strategy })}
                onClose={() => setEditing(null)}
              />
            ) : (
              <Bullets
                items={(selectedCircle.strategy || []).map((item) => item.content)}
                color="bg-amber-500"
                empty="Aucune orientation définie."
              />
            )}
          </Section>
        )}

        {!isLeafRole && (
          <Section
            title={`Rôles (${directRoles.length})`}
            hint={directRoles.length > 0 ? 'Le focus de chacun (ses projets) se lit et se modifie dans la fiche du rôle.' : null}
          >
            {directRoles.length > 0 ? (
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                {directRoles.map((role) => {
                  const roleOwners = ownersOf(role);
                  return (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => onSelectCircle && onSelectCircle(role)}
                      className="group flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-50 first:rounded-t-xl last:rounded-b-xl"
                      title="Voir le rôle : redevabilités, domaines et focus de chacun"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700">{role.name}</span>
                          <ReservedDomainChips keys={role.reserved_domains} />
                        </div>
                        {roleOwners.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {roleOwners.map((id) => {
                              const person = findEmployee(id);
                              const count = splitFocusProjects(roleFocusMap[`${role.id}:${id}`] || '', projectById).current.length;
                              return (
                                <span key={id} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                                  <Avatar employee={person} size={18} />
                                  {person?.name?.split(' ')[0] || '?'}
                                  {count > 0 && (
                                    <span className="text-slate-400" title="Projets en cours de son focus">
                                      · {count} en cours
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-amber-700">Rôle vacant.</p>
                        )}
                      </div>
                      <ChevronRight size={16} className="mt-0.5 shrink-0 text-slate-300 group-hover:text-indigo-500" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Aucun rôle dans ce cercle.</p>
            )}
          </Section>
        )}

        {!isLeafRole && subCircles.length > 0 && (
          <Section title="Sous-cercles">
            <div className="flex flex-wrap gap-2">
              {subCircles.map((circle) => (
                <button
                  key={circle.id}
                  type="button"
                  onClick={() => onSelectCircle && onSelectCircle(circle)}
                  className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                >
                  {circle.name}
                </button>
              ))}
            </div>
          </Section>
        )}

        {!isLeafRole && (
          <Section title={`Membres (${circleMemberIds.length})`}>
            {circleMemberIds.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {circleMemberIds.map((id) => {
                  const person = findEmployee(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onSelectEmployee && onSelectEmployee(id)}
                      className="flex items-center gap-1.5 rounded-full border border-slate-200 py-0.5 pl-0.5 pr-2.5 hover:bg-slate-50"
                      title="Voir ses rôles"
                    >
                      <Avatar employee={person} size={22} />
                      <span className="text-xs font-medium text-slate-700">{person?.name?.split(' ')[0] || '?'}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Personne.</p>
            )}
          </Section>
        )}

        {canViewProjects && (
          <Section title={`Projets en cours (${inProgressProjects.length})`}>
            {inProgressProjects.length > 0 ? (
              <div className="space-y-1.5">
                {inProgressProjects.map((project) => (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => onOpenProject && onOpenProject(project.id)}
                    className="w-full flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left hover:border-indigo-200"
                  >
                    <span className="text-sm font-medium text-slate-800 truncate">{project.name}</span>
                    <span className="shrink-0 text-[11px] text-slate-400">
                      {project.project_number !== undefined && project.project_number !== null ? `#${project.project_number} · ` : ''}
                      {statusLabel(project.status)}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Aucun projet en cours.</p>
            )}
          </Section>
        )}

        {!isLeafRole && (
          <CircleMetrics
            metricsRange={metricsRange}
            metricsRangeValid={metricsRangeValid}
            onMetricsRangeChange={onMetricsRangeChange}
            currentSlotRange={currentSlotRange}
            isUsingSlotRange={isUsingSlotRange}
            summary={circleMetricsSummary}
            loading={circleMetricsLoading}
            error={circleMetricsError}
          />
        )}
      </div>
    </>,
  );
};

export default StructurePanel;
